import React from 'react';
import { Button, Card, Select, Space, Table } from 'antd';
import { useTranslation } from 'react-i18next';
import type { PokerSiteId } from '@poker-stats/contracts';
import { useMiningUpload } from './useMiningUpload';
import type { MiningUploadItem } from './types';
import { formatMiningUploadSize, miningUploadRoomOptions } from './presentation';
import './MiningUploadPage.css';

const MiningUploadPage: React.FC = () => {
  const { t, i18n } = useTranslation('translation');
  const upload = useMiningUpload();
  const fileInput = React.useRef<HTMLInputElement>(null);
  const folderInput = React.useRef<HTMLInputElement | null>(null);
  const roomLabelId = React.useId();
  const extension = upload.room === 6 ? '.xml' : '.txt';
  const pendingCount = upload.items.filter((item) => item.status === 'pending').length;
  const uploadedCount = upload.items.filter((item) => item.status === 'uploaded').length;
  const failedCount = upload.items.filter((item) => item.status === 'error').length;

  const addSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    upload.addFiles(Array.from(event.currentTarget.files ?? []));
    event.currentTarget.value = '';
  };

  return (
    <Card className="MiningUploadPage" title={t('admin_mining_upload')}>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        accept={extension}
        disabled={upload.isUploading}
        onChange={addSelection}
      />
      <input
        ref={(element) => {
          folderInput.current = element;
          element?.setAttribute('webkitdirectory', '');
        }}
        type="file"
        multiple
        hidden
        disabled={upload.isUploading}
        onChange={addSelection}
      />
      <Space orientation="vertical" size={20} className="MiningUploadPage__Stack">
        <div className="MiningUploadPage__Controls">
          <div className="MiningUploadPage__Room">
            <label id={roomLabelId}>{t('admin_mining_upload_room')}</label>
            <Select<PokerSiteId>
              aria-labelledby={roomLabelId}
              value={upload.room}
              options={[...miningUploadRoomOptions]}
              onChange={upload.setRoom}
              disabled={upload.isUploading || upload.items.length > 0}
            />
          </div>
          <Space wrap>
            <Button disabled={upload.isUploading} onClick={() => fileInput.current?.click()}>
              {t('admin_mining_upload_add_files')}
            </Button>
            <Button disabled={upload.isUploading} onClick={() => folderInput.current?.click()}>
              {t('admin_mining_upload_add_folder')}
            </Button>
          </Space>
        </div>
        <div className="MiningUploadPage__Actions">
          <Space wrap>
            <Button
              type="primary"
              loading={upload.isUploading}
              disabled={upload.isUploading || pendingCount === 0}
              onClick={upload.startUpload}
            >
              {t('admin_mining_upload_start')}
            </Button>
            <Button disabled={upload.isUploading || failedCount === 0} onClick={upload.retryErrors}>
              {t('admin_mining_upload_retry')}
            </Button>
            <Button
              disabled={
                upload.isUploading || (upload.items.length === 0 && upload.skippedCount === 0)
              }
              onClick={upload.clearQueue}
            >
              {t('admin_mining_upload_clear')}
            </Button>
          </Space>
          <p className="MiningUploadPage__Summary" role="status" aria-live="polite">
            {t('admin_mining_upload_summary', {
              uploaded: uploadedCount,
              total: upload.items.length,
              failed: failedCount,
            })}
            {upload.skippedCount > 0
              ? ` · ${t('admin_mining_upload_skipped', { count: upload.skippedCount })}`
              : null}
          </p>
        </div>
        <Table<MiningUploadItem>
          rowKey="id"
          dataSource={upload.items}
          scroll={{ x: 640 }}
          pagination={{ pageSize: 50, showSizeChanger: false, hideOnSinglePage: true }}
          locale={{ emptyText: t('admin_mining_upload_empty') }}
          columns={[
            {
              key: 'file',
              title: t('admin_mining_upload_file'),
              render: (_value: unknown, item: MiningUploadItem) => (
                <div className="MiningUploadPage__File">
                  <strong>{item.name}</strong>
                  {item.relativePath !== item.name ? <small>{item.relativePath}</small> : null}
                </div>
              ),
            },
            {
              key: 'size',
              title: t('admin_mining_upload_size'),
              width: 120,
              render: (_value: unknown, item: MiningUploadItem) =>
                formatMiningUploadSize(item.size, i18n.resolvedLanguage ?? i18n.language),
            },
            {
              key: 'status',
              title: t('admin_mining_upload_status'),
              width: 240,
              render: (_value: unknown, item: MiningUploadItem) => (
                <div
                  className={`MiningUploadPage__Status MiningUploadPage__Status--${item.status}`}
                >
                  <span>
                    {item.status === 'uploaded' ? <span aria-hidden="true">✓ </span> : null}
                    {t(`admin_mining_upload_status_${item.status}`)}
                  </span>
                  {item.error ? <small>{item.error}</small> : null}
                </div>
              ),
            },
          ]}
        />
      </Space>
    </Card>
  );
};

export default MiningUploadPage;
